/**
 * ClassificaTabella — tabella classifica (mobile 2 righe / desktop 1 riga).
 *
 * Estratta da LegaPage (Andrea 2026-09-09) per usare la stessa tabella
 * per il campionato e per ognuno dei gironi di Champions. Le righe
 * arrivano già normalizzate e ordinate (features/common/classifica.ts):
 * qui si disegnano soltanto, comprese le squadre ancora a zero.
 */

import { useState } from "react";
import { MagliaSquadra } from "../common/MagliaSquadra";
import { TrofeiSquadra } from "../common/TrofeiSquadra";
import { mediaAPartita, type RigaClassifica } from "../common/classifica";
import { tr } from "../../i18n";

interface ClassificaTabellaProps {
  righe: RigaClassifica[];
  nomeSquadra: (id: string) => string;
  /** Quante squadre passano il turno: le prime N hanno il bordo accent. */
  qualificate?: number;
  /** Titolo interno (usato dai gironi: "Girone A"). */
  titolo?: string;
}

/** "—" finché la squadra non ha giocato: uno 0 secco sembrerebbe un
 * risultato vero (Andrea 2026-09-09). */
function val(giocato: boolean, v: number, decimali = 0): string {
  if (!giocato) return "—";
  return decimali > 0 ? v.toFixed(decimali) : String(v);
}

/** Media a partita con un decimale in piu' del totale; "—" senza partite. */
function media(r: RigaClassifica, totale: number): string {
  const m = mediaAPartita(totale, r.partite_giocate);
  return !r.giocato || m === null ? "—" : m.toFixed(2);
}

export function ClassificaTabella({
  righe,
  nomeSquadra,
  qualificate,
  titolo,
}: ClassificaTabellaProps) {
  const qualificata = (pos: number) => !!qualificate && pos <= qualificate;
  // Andrea 2026-09-17: su mobile fantapunti subiti e medie si aprono
  // premendo sulla squadra, una per volta — tutte aperte la classifica era
  // troppo piena. Aprendone un'altra la prima si richiude.
  const [aperta, setAperta] = useState<string | null>(null);

  return (
    <div className="card p-0 overflow-hidden">
      {titolo && (
        <div className="px-3 py-2 border-b border-primary-soft flex items-center justify-between gap-2">
          <span className="font-bold text-accent">{titolo}</span>
          {qualificate ? (
            <span className="text-[10px] text-fg-muted uppercase tracking-wider">
              prime {qualificate} qualificate
            </span>
          ) : null}
        </div>
      )}

      {/* === Mobile: 2 righe per squadra (nome sopra, stats sotto) === */}
      <div className="md:hidden">
        {/* Andrea 2026-10-22: colonne FISSE e uguali per i contatori (ci
            stanno 2 cifre), numeri centrati cosi' "10" e "3" non si toccano;
            poi Pti e in fondo FP TOT e FP VS (fantapunti subiti). */}
        <div className="bg-primary-deep/40 px-2 py-2 grid grid-cols-[1.4rem_1.4rem_1.4rem_1.4rem_1.5rem_1.5rem_1.9rem_1.9rem_minmax(2.9rem,1fr)_minmax(2.9rem,1fr)] gap-1 text-[9px] uppercase tracking-wider font-bold text-fg-muted">
          <span className="text-center">G</span>
          <span className="text-center" title={tr("Vittorie")}>
            V
          </span>
          <span className="text-center" title={tr("Pareggi")}>
            P
          </span>
          <span className="text-center" title={tr("Sconfitte")}>
            S
          </span>
          <span className="text-center" title={tr("Gol fatti")}>
            G+
          </span>
          <span className="text-center" title={tr("Gol subiti")}>
            G-
          </span>
          <span className="text-center" title={tr("Differenza reti")}>
            +/-
          </span>
          <span className="text-center text-accent">Pti</span>
          <span className="text-right" title={tr("Fantapunti totali fatti")}>
            FP TOT
          </span>
          <span className="text-right" title={tr("Fantapunti totali subiti")}>
            FP VS
          </span>
        </div>
        {righe.length === 0 && (
          <div className="p-6 text-center text-fg-muted">
            {tr("Nessuna squadra in questa competizione.")}
          </div>
        )}
        {righe.map((r) => (
          <div
            key={r.squadra_id}
            role="button"
            tabIndex={0}
            aria-expanded={aperta === r.squadra_id}
            onClick={() => setAperta((a) => (a === r.squadra_id ? null : r.squadra_id))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setAperta((a) => (a === r.squadra_id ? null : r.squadra_id));
              }
            }}
            className={`px-2 py-2 border-b border-primary/10 last:border-b-0 cursor-pointer transition-colors ${
              aperta === r.squadra_id ? "bg-primary-deep/30" : ""
            } ${qualificata(r.posizione) ? "border-l-2 border-l-accent" : ""}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-display text-fg-muted w-7 shrink-0">{r.posizione}°</span>
              <MagliaSquadra squadraId={r.squadra_id} size={22} />
              <span className="font-bold text-fg break-words">{nomeSquadra(r.squadra_id)}</span>
              <TrofeiSquadra squadraId={r.squadra_id} size="sm" />
              <span
                aria-hidden="true"
                className={`ml-auto shrink-0 text-fg-muted text-xs transition-transform ${
                  aperta === r.squadra_id ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </div>
            {/* Andrea 2026-10-16: numeri grigi, tranne V verde, P (pareggi)
                bianco, S rossa e Pti oro. */}
            <div className="grid grid-cols-[1.4rem_1.4rem_1.4rem_1.4rem_1.5rem_1.5rem_1.9rem_1.9rem_minmax(2.9rem,1fr)_minmax(2.9rem,1fr)] gap-1 text-[13px] tabular-nums">
              <span className="text-center font-mono text-fg-muted">{r.partite_giocate}</span>
              <span className="text-center font-mono text-success">{val(r.giocato, r.vinte)}</span>
              <span className="text-center font-mono text-fg">{val(r.giocato, r.pareggiate)}</span>
              <span className="text-center font-mono text-danger">{val(r.giocato, r.perse)}</span>
              <span className="text-center font-mono text-fg-muted">
                {val(r.giocato, r.gol_fatti)}
              </span>
              <span className="text-center font-mono text-fg-muted">
                {val(r.giocato, r.gol_subiti)}
              </span>
              <span className="text-center font-mono text-fg-muted">
                {r.giocato ? `${r.diff_reti > 0 ? "+" : ""}${r.diff_reti}` : "—"}
              </span>
              <span className="text-center font-mono font-bold text-accent">
                {val(r.giocato, r.punti)}
              </span>
              <span className="text-right font-mono text-fg-muted">
                {val(r.giocato, r.fantapunti, 1)}
              </span>
              <span className="text-right font-mono text-fg-muted">
                {val(r.giocato, r.fantapunti_subiti, 1)}
              </span>
            </div>
            {/* Andrea 2026-09-17: fantapunti subiti e medie a partita. A
                375px altre tre colonne non ci stanno: riga a parte, solo
                per la squadra aperta. */}
            {aperta === r.squadra_id && (
              <div className="mt-1.5 pt-1.5 border-t border-primary/10 flex flex-wrap justify-end gap-x-3 gap-y-0.5 text-[11px] text-fg-muted">
                <span>
                  media FP{" "}
                  <span className="font-mono tabular-nums text-fg">{media(r, r.fantapunti)}</span>
                </span>
                <span>
                  media subiti{" "}
                  <span className="font-mono tabular-nums text-fg">
                    {media(r, r.fantapunti_subiti)}
                  </span>
                </span>
              </div>
            )}
          </div>
        ))}
        {righe.length > 0 && (
          <div className="px-3 py-1.5 text-[10px] text-fg-muted text-center border-t border-primary/10">
            {tr("Premi su una squadra per le medie a partita.")}
          </div>
        )}
      </div>

      {/* === Desktop: tabella a una riga === */}
      <div className="hidden md:block">
        <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider flex gap-2">
          <span className="w-8">#</span>
          <span className="flex-1">{tr("Squadra")}</span>
          <span className="w-12 text-right">G</span>
          <span className="w-12 text-right" title={tr("Vittorie")}>
            V
          </span>
          <span className="w-12 text-right" title={tr("Pareggi")}>
            P
          </span>
          <span className="w-12 text-right" title={tr("Sconfitte")}>
            S
          </span>
          <span className="w-12 text-right" title={tr("Gol fatti")}>
            G+
          </span>
          <span className="w-12 text-right" title={tr("Gol subiti")}>
            G-
          </span>
          <span className="w-16 text-right" title={tr("Differenza reti")}>
            +/-
          </span>
          <span className="w-20 text-right" title={tr("Fantapunti totali fatti")}>
            FP TOT
          </span>
          <span className="w-20 text-right" title={tr("Fantapunti totali subiti")}>
            FP VS
          </span>
          <span className="w-16 text-right" title={tr("Fantapunti fatti, media a partita")}>
            Ø FP
          </span>
          <span className="w-16 text-right" title={tr("Fantapunti subiti, media a partita")}>
            Ø VS
          </span>
          <span className="w-12 text-right">Pti</span>
        </div>
        {righe.length === 0 && (
          <div className="p-6 text-center text-fg-muted">
            {tr("Nessuna squadra in questa competizione.")}
          </div>
        )}
        {righe.map((r) => (
          <div
            key={r.squadra_id}
            className={`flex gap-2 px-4 py-2 border-b border-primary/10 last:border-b-0 text-sm ${
              qualificata(r.posizione) ? "border-l-2 border-l-accent" : ""
            }`}
          >
            <span className="w-8 text-fg-muted">{r.posizione}°</span>
            <span className="flex-1 font-bold truncate flex items-center gap-1.5">
              <MagliaSquadra squadraId={r.squadra_id} size={20} />
              <span className="truncate">{nomeSquadra(r.squadra_id)}</span>
              <TrofeiSquadra squadraId={r.squadra_id} size="sm" />
            </span>
            {/* Andrea 2026-10-16: numeri grigi, tranne V verde, P (pareggi)
                bianco, S rossa e Pti oro. */}
            <span className="w-12 text-right font-mono text-fg-muted">{r.partite_giocate}</span>
            <span className="w-12 text-right font-mono text-success">
              {val(r.giocato, r.vinte)}
            </span>
            <span className="w-12 text-right font-mono text-fg">
              {val(r.giocato, r.pareggiate)}
            </span>
            <span className="w-12 text-right font-mono text-danger">{val(r.giocato, r.perse)}</span>
            <span className="w-12 text-right font-mono text-fg-muted">
              {val(r.giocato, r.gol_fatti)}
            </span>
            <span className="w-12 text-right font-mono text-fg-muted">
              {val(r.giocato, r.gol_subiti)}
            </span>
            <span className="w-16 text-right font-mono text-fg-muted">
              {r.giocato ? `${r.diff_reti > 0 ? "+" : ""}${r.diff_reti}` : "—"}
            </span>
            <span className="w-20 text-right font-mono text-fg-muted">
              {val(r.giocato, r.fantapunti, 2)}
            </span>
            <span className="w-20 text-right font-mono text-fg-muted">
              {val(r.giocato, r.fantapunti_subiti, 2)}
            </span>
            <span className="w-16 text-right font-mono text-fg-muted">
              {media(r, r.fantapunti)}
            </span>
            <span className="w-16 text-right font-mono text-fg-muted">
              {media(r, r.fantapunti_subiti)}
            </span>
            <span className="w-12 text-right font-mono font-bold text-accent">
              {val(r.giocato, r.punti)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
